import { useState, type ReactNode } from 'react';
import {
  AutoComplete,
  type AutoCompleteChangeEvent,
  type AutoCompleteCompleteEvent,
  type AutoCompleteSelectEvent,
} from 'primereact/autocomplete';
import api from '@/lib/api';

/**
 * Reusable searchable selector for entity references (foreign keys).
 *
 * GOVERNANCE: Never ask a user to type/paste a raw entity ID into a free-text input.
 * Use this component (large/unbounded sets) or a <Dropdown> (small bounded sets) instead.
 *
 * Wraps the approved PrimeReact AutoComplete. Queries `endpoint` with `?<searchParam>=` and
 * expects a paginated `{ data: T[] }` response (falls back to a raw array). `forceSelection`
 * guarantees the bound value is always a real entity (or null), never arbitrary text.
 */
export interface EntityAutocompleteProps<T> {
  /** API path to search, e.g. '/sales', '/products', '/users'. */
  endpoint: string;
  /** Currently selected entity, or null when nothing is selected. */
  value: T | null;
  /** Called with the selected entity, or null when cleared. */
  onChange: (value: T | null) => void;
  /** Human-readable label for an entity (shown in the input and as the default suggestion text). */
  getLabel: (item: T) => string;
  /** Query-string param used for the search term. Defaults to 'search'. */
  searchParam?: string;
  /** Extra static query params merged into every request. */
  extraParams?: Record<string, string | number | boolean>;
  /** Max suggestions to fetch. Defaults to 20. */
  limit?: number;
  /** Minimum characters before searching. Defaults to 1. */
  minChars?: number;
  /** Optional rich suggestion renderer. */
  itemTemplate?: (item: T) => ReactNode;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  /** Show the dropdown trigger button. Defaults to true. */
  dropdown?: boolean;
  id?: string;
}

type LabeledRecord = Record<string, unknown> & { __label: string };

const LABEL_KEY = '__label';

export function EntityAutocomplete<T extends object>({
  endpoint,
  value,
  onChange,
  getLabel,
  searchParam = 'search',
  extraParams,
  limit = 20,
  minChars = 1,
  itemTemplate,
  placeholder,
  disabled,
  className,
  inputClassName = 'w-full rounded-md border bg-background px-3 py-2 text-sm',
  dropdown = true,
  id,
}: EntityAutocompleteProps<T>) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<LabeledRecord[]>([]);

  const label = (item: T): LabeledRecord => ({
    ...(item as Record<string, unknown>),
    [LABEL_KEY]: getLabel(item),
  });

  const strip = (item: LabeledRecord): T => {
    const next: Record<string, unknown> = { ...item };
    delete next[LABEL_KEY];
    return next as unknown as T;
  };

  const completeMethod = async (e: AutoCompleteCompleteEvent) => {
    const q = (e.query ?? '').trim();
    if (q.length < minChars) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await api.get(endpoint, {
        params: { [searchParam]: q, limit, page: 1, ...extraParams },
      });
      const items: T[] = res.data?.data ?? res.data ?? [];
      setSuggestions(items.map((it) => label(it)));
    } catch {
      setSuggestions([]);
    }
  };

  const display: LabeledRecord | string = value ? label(value) : query;

  return (
    <AutoComplete
      id={id}
      value={display}
      field={LABEL_KEY}
      suggestions={suggestions}
      completeMethod={completeMethod}
      delay={300}
      dropdown={dropdown}
      forceSelection
      disabled={disabled}
      placeholder={placeholder}
      className={className}
      inputClassName={inputClassName}
      itemTemplate={itemTemplate ? (item: LabeledRecord) => itemTemplate(strip(item)) : undefined}
      onChange={(e: AutoCompleteChangeEvent) => {
        if (typeof e.value === 'string') {
          setQuery(e.value);
          if (e.value === '' && value) onChange(null);
        } else if (e.value) {
          setQuery('');
          onChange(strip(e.value as LabeledRecord));
        }
      }}
      onSelect={(e: AutoCompleteSelectEvent) => {
        setQuery('');
        onChange(strip(e.value as LabeledRecord));
      }}
    />
  );
}

export default EntityAutocomplete;
